import {
  Alert,
  AlertTitle,
  Button,
  List,
  Stack,
  Typography,
} from "@mui/material";
import { FacilityTerminalCard } from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/FacilityTerminalCard";
import {
  type TerminalCountChange,
  TerminalCountChangeRequestModal,
} from "Components/Shared/Merchant/Products/AppProducts/ProductAccordion/AccordionContent/TerminalCountChangeRequestModal";
import {
  TerminalFinancingModel,
  type MerchantFacilityDto,
  type TerminalCountChangeRequestDto,
} from "Infrastructure/Api/Api";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.OrbitL.FacilityTerminals;
const REQUEST_RESOURCES =
  Resources.Components.Merchant.OrbitL.TerminalCountChange;

type Props = {
  merchantPublicID: string;
  facilities: Array<MerchantFacilityDto>;
  financingModel: TerminalFinancingModel;
  /** Order is already with Comgate — counts move only through a request to support. */
  isTerminalCountLocked?: boolean;
  openRequest?: TerminalCountChangeRequestDto | null;
  hasOrbitLProduct: boolean;
  hasSoftPosProduct: boolean;
};

export const OrbitLFacilityTerminals = ({
  merchantPublicID,
  facilities,
  financingModel,
  isTerminalCountLocked = false,
  openRequest,
  hasOrbitLProduct,
  hasSoftPosProduct,
}: Props) => {
  const { t } = useResource();
  const [expandedFacilityID, setExpandedFacilityID] = useState<string | null>(
    null,
  );
  const [draftCounts, setDraftCounts] = useState<Record<string, number>>({});
  const [draftSoftPosCounts, setDraftSoftPosCounts] = useState<
    Record<string, number>
  >({});
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  // Bumped after a request is sent so the cards remount and their inputs fall back to the real
  // counts — the request does not change them, so leaving the drafts on screen would lie.
  const [draftResetToken, setDraftResetToken] = useState(0);

  if (facilities.length === 0) {
    return null;
  }

  const isHunterFinanced = financingModel === TerminalFinancingModel.Hunter;
  const hasOpenRequest = !!openRequest;

  const currentTotal = facilities.reduce(
    (total, facility) => total + (facility.terminalCount ?? 0),
    0,
  );
  const currentSoftPosTotal = facilities.reduce(
    (total, facility) => total + (facility.softPosCount ?? 0),
    0,
  );

  // SoftPOS licences ride along for merchants who hold the product. Once the order is locked they
  // ride along for everyone: the request is the only way left to get SoftPOS onto the contract,
  // and support activates the product when it confirms the request.
  const canRequestSoftPos = hasSoftPosProduct || isTerminalCountLocked;
  const changes: Array<TerminalCountChange> = facilities
    .map((facility) => {
      const currentCount = facility.terminalCount ?? 0;
      const currentSoftPosCount = facility.softPosCount ?? 0;
      return {
        facilityPublicID: facility.publicID,
        facilityName: facility.name,
        currentCount,
        requestedCount: draftCounts[facility.publicID] ?? currentCount,
        currentSoftPosCount: canRequestSoftPos
          ? currentSoftPosCount
          : undefined,
        requestedSoftPosCount: canRequestSoftPos
          ? (draftSoftPosCounts[facility.publicID] ?? currentSoftPosCount)
          : undefined,
      };
    })
    .filter(
      (change) =>
        change.requestedCount !== change.currentCount ||
        change.requestedSoftPosCount !== change.currentSoftPosCount,
    );

  const handleRequested = () => {
    setIsRequestModalOpen(false);
    setDraftCounts({});
    setDraftSoftPosCounts({});
    setDraftResetToken((token) => token + 1);
  };

  return (
    <Stack gap={1} mt={2}>
      <Typography variant="subtitle2" fontWeight={600}>
        {t(RESOURCES.Title)}
      </Typography>

      {hasOpenRequest && (
        <Alert severity="info">
          <AlertTitle>{t(REQUEST_RESOURCES.PendingTitle)}</AlertTitle>
          {t(REQUEST_RESOURCES.PendingMessage, {
            from: openRequest.oldTotal,
            to: openRequest.newTotal,
          })}
          {openRequest.oldSoftPosTotal != null && (
            <Typography variant="body2" mt={0.5}>
              {t(REQUEST_RESOURCES.PendingSoftPosMessage, {
                from: openRequest.oldSoftPosTotal,
                to: openRequest.newSoftPosTotal,
              })}
            </Typography>
          )}
          {openRequest.note && (
            <Typography variant="body2" mt={0.5}>
              {t(REQUEST_RESOURCES.PendingNote, { note: openRequest.note })}
            </Typography>
          )}
        </Alert>
      )}

      {isTerminalCountLocked && !hasOpenRequest && (
        <Alert severity="info">{t(REQUEST_RESOURCES.LockedHint)}</Alert>
      )}

      <List disablePadding>
        {facilities.map((facility) => (
          <FacilityTerminalCard
            key={`${facility.publicID}-${draftResetToken}`}
            facility={facility}
            merchantPublicID={merchantPublicID}
            isHunterFinanced={isHunterFinanced}
            isCountLocked={isTerminalCountLocked}
            hasOrbitLProduct={hasOrbitLProduct}
            hasSoftPosProduct={hasSoftPosProduct}
            canRequestSoftPos={canRequestSoftPos}
            isExpanded={expandedFacilityID === facility.publicID}
            onToggle={() =>
              setExpandedFacilityID((prev) =>
                prev === facility.publicID ? null : facility.publicID,
              )
            }
            onDraftCountChange={(count) =>
              setDraftCounts((prev) => ({
                ...prev,
                [facility.publicID]: count,
              }))
            }
            onDraftSoftPosCountChange={(count) =>
              setDraftSoftPosCounts((prev) => ({
                ...prev,
                [facility.publicID]: count,
              }))
            }
          />
        ))}
      </List>

      {isTerminalCountLocked && (
        <Button
          variant="contained"
          size="small"
          disabled={hasOpenRequest || changes.length === 0}
          onClick={() => setIsRequestModalOpen(true)}
          sx={{ alignSelf: "flex-start" }}
        >
          {t(REQUEST_RESOURCES.RequestButton)}
        </Button>
      )}

      {isRequestModalOpen && (
        <TerminalCountChangeRequestModal
          merchantPublicID={merchantPublicID}
          changes={changes}
          currentTotal={currentTotal}
          currentSoftPosTotal={
            canRequestSoftPos ? currentSoftPosTotal : undefined
          }
          onClose={() => setIsRequestModalOpen(false)}
          onRequested={handleRequested}
        />
      )}
    </Stack>
  );
};
